// =============================================================================
// UploadPropertiesModal.jsx — KlassDoc Sprint 6.5 (refactored Wave 2.1)
// -----------------------------------------------------------------------------
// iManage-style "New Document Properties" dialogue.
// Sub-components (AccessLevelDropdown, PrincipalPicker, AclEntryRow) extracted
// to ./security/SecurityControls.jsx for reuse by ManageSecurityModal.
// =============================================================================

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import * as lucide from "lucide-react";
import { PrincipalPicker, AclEntryRow } from "./security/SecurityControls";

const CONFIDENTIALITY_LEVELS = [
  { value: "public",       labelFr: "Public",       labelEn: "Public",       color: "#5CB88A", icon: "Globe",       desc: { fr: "Visible à tous", en: "Visible to everyone" } },
  { value: "internal",     labelFr: "Interne",      labelEn: "Internal",     color: "#5E8AD4", icon: "Building",    desc: { fr: "Personnel autorisé uniquement", en: "Authorized staff only" } },
  { value: "confidential", labelFr: "Confidentiel", labelEn: "Confidential", color: "#D4B85E", icon: "Lock",        desc: { fr: "Accès restreint", en: "Restricted access" } },
  { value: "secret",       labelFr: "Secret",       labelEn: "Secret",       color: "#D46A6A", icon: "ShieldAlert", desc: { fr: "Très haute confidentialité", en: "Highest confidentiality" } },
];

const DOC_CLASSES = [
  { value: "contract",       labelFr: "Contrat",            labelEn: "Contract" },
  { value: "report",         labelFr: "Rapport",            labelEn: "Report" },
  { value: "invoice",        labelFr: "Facture",            labelEn: "Invoice" },
  { value: "correspondence", labelFr: "Correspondance",     labelEn: "Correspondence" },
  { value: "legal",          labelFr: "Document juridique", labelEn: "Legal Document" },
  { value: "financial",      labelFr: "Document financier", labelEn: "Financial Document" },
  { value: "hr",             labelFr: "Ressources humaines", labelEn: "Human Resources" },
  { value: "technical",      labelFr: "Document technique", labelEn: "Technical Document" },
  { value: "policy",         labelFr: "Politique/Procédure", labelEn: "Policy/Procedure" },
  { value: "other",          labelFr: "Autre",              labelEn: "Other" },
];

function fmt(lang, fr, en) { return lang === "fr" ? fr : en; }
function formatBytes(b) { if (!b) return "0 B"; const k=1024, s=["B","KB","MB","GB"], i=Math.floor(Math.log(b)/Math.log(k)); return parseFloat((b/Math.pow(k,i)).toFixed(1)) + " " + s[i]; }

export default function UploadPropertiesModal({ file, folderId, onClose, onSuccess, api, currentUser, lang = "fr", THEME }) {
  const [activeTab, setActiveTab] = useState("properties");
  const [departments, setDepartments] = useState([]);
  const [folders, setFolders] = useState([]);
  // Folder the document will be filed into. Initialized from the folderId
  // prop (set when the user uploads from inside a folder) but editable via
  // the picker below so the destination can be changed at upload time.
  const [selectedFolderId, setSelectedFolderId] = useState(folderId != null ? String(folderId) : "");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const [props, setProps] = useState({
    title: file?.name || "",
    author: currentUser?.name || "",
    department_id: "",
    doc_class: "",
    confidentiality: "internal",
    keywords: [],
    retention_date: "",
    notes: "",
  });
  const [keywordInput, setKeywordInput] = useState("");

  const [aclEntries, setAclEntries] = useState([
    { principal_type: "user",  principal_id: currentUser?.id, principal_name: currentUser?.name, access_level: "full", locked: true },
    { principal_type: "group", principal_id: null,            principal_name: "Administrators",  access_level: "full", locked: true, pendingGroupLookup: true },
  ]);

  useEffect(() => {
    api.get("/departments").then(r => setDepartments(r.data || [])).catch(() => {});
    api.get("/folders").then(r => setFolders(r.data || [])).catch(() => {});
    api.get("/groups").then(r => {
      const admins = (r.data || []).find(g => g.name === "administrators");
      if (admins) {
        setAclEntries(prev => prev.map(e => e.pendingGroupLookup ? { ...e, principal_id: admins.id, pendingGroupLookup: false } : e));
      }
    }).catch(() => {});
  }, [api]);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape" && !uploading) onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, uploading]);

  // Flatten the folder tree into indented <option> rows (root → leaves),
  // matching the move-document picker in App.jsx. Each level adds two
  // non-breaking spaces plus an "└ " marker so nesting reads in a native
  // <select> (which can't render real indentation).
  function folderOptions() {
    const build = (parentId, depth) =>
      folders
        .filter(f => (f.parent_id || null) === (parentId || null))
        .flatMap(f => [
          { id: f.id, label: "  ".repeat(depth) + (depth > 0 ? "└ " : "") + f.name },
          ...build(f.id, depth + 1),
        ]);
    return build(null, 0);
  }

  function addKeyword() {
    const k = keywordInput.trim();
    if (k && !props.keywords.includes(k)) {
      setProps(p => ({ ...p, keywords: [...p.keywords, k] }));
    }
    setKeywordInput("");
  }
  function removeKeyword(k) { setProps(p => ({ ...p, keywords: p.keywords.filter(x => x !== k) })); }

  function addPrincipal(p) { setAclEntries(prev => [...prev, p]); }
  function updatePrincipalAccess(idx, level) {
    setAclEntries(prev => prev.map((e, i) => i === idx ? { ...e, access_level: level } : e));
  }
  function removePrincipal(idx) {
    setAclEntries(prev => prev.filter((_, i) => i !== idx));
  }

  const excludeIds = new Set(aclEntries.map(e => `${e.principal_type}-${e.principal_id}`));

  async function handleUpload() {
    setError(null);
    if (!props.title.trim()) { setError(fmt(lang, "Le titre est requis", "Title is required")); setActiveTab("properties"); return; }
    if (!file) { setError(fmt(lang, "Aucun fichier sélectionné", "No file selected")); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (selectedFolderId) fd.append("folder_id", selectedFolderId);
      const uploaded = await api.upload("/documents/upload", fd);
      const docId = uploaded?.id;
      if (!docId) throw new Error("Upload returned no document ID");

      try {
        await api.put(`/metadata/${docId}`, {
          author: props.author,
          department: departments.find(d => d.id === parseInt(props.department_id))?.name || null,
          doc_class: props.doc_class || null,
          confidentiality: props.confidentiality,
          keywords: props.keywords,
          retention_date: props.retention_date || null,
        });
      } catch (e) { console.warn("Metadata save failed (non-fatal):", e); }

      const userEntries = aclEntries.filter(e => !e.locked && e.principal_id);
      if (userEntries.length > 0) {
        const allEntries = aclEntries.filter(e => e.principal_id).map(e => ({
          principal_type: e.principal_type,
          principal_id: e.principal_id,
          principal_name: e.principal_name,
          access_level: e.access_level,
        }));
        try {
          await api.post(`/permissions/document/${docId}`, { entries: allEntries });
        } catch (e) { console.warn("ACL save failed (non-fatal):", e); }
      }

      onSuccess(uploaded);
    } catch (e) {
      console.error("Upload error:", e);
      setError(e.message || fmt(lang, "Échec du téléversement", "Upload failed"));
    } finally {
      setUploading(false);
    }
  }

  const conf = CONFIDENTIALITY_LEVELS.find(c => c.value === props.confidentiality) || CONFIDENTIALITY_LEVELS[1];
  const FONT = "'DM Sans','Satoshi',-apple-system,sans-serif";
  const fieldStyle = { padding: "10px 14px", background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 8, color: THEME.text, fontSize: 13, outline: "none", fontFamily: FONT, width: "100%" };
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 600, color: THEME.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.3 };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: FONT }}
      onClick={e => { if (e.target === e.currentTarget && !uploading) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
        style={{ background: THEME.surfaceRaised, border: `1px solid ${THEME.border}`, borderRadius: 16, width: "100%", maxWidth: 720, maxHeight: "92vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 32px 96px rgba(0,0,0,0.6)" }}
      >
        {/* HEADER */}
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${THEME.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 11, background: `linear-gradient(135deg, ${THEME.accent}, ${THEME.accent}CC)`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 16px ${THEME.accent}40` }}>
              <lucide.FilePlus size={22} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: THEME.text }}>{fmt(lang, "Nouveau document", "New Document Properties")}</div>
              <div style={{ fontSize: 12, color: THEME.textDim, marginTop: 2 }}>{file?.name} · {formatBytes(file?.size)}</div>
            </div>
          </div>
          <button onClick={() => !uploading && onClose()} disabled={uploading} style={{ background: "transparent", border: "none", cursor: uploading ? "not-allowed" : "pointer", padding: 6, opacity: uploading ? 0.4 : 1 }}>
            <lucide.X size={20} color={THEME.textMuted} />
          </button>
        </div>

        {/* TABS */}
        <div style={{ display: "flex", borderBottom: `1px solid ${THEME.border}`, padding: "0 28px" }}>
          {[
            { id: "properties", label: fmt(lang, "Propriétés", "Properties"), icon: lucide.Info },
            { id: "security",   label: fmt(lang, "Sécurité",   "Security"),   icon: lucide.Shield },
          ].map(t => {
            const isA = activeTab === t.id;
            return (
              <button key={t.id} type="button" onClick={() => setActiveTab(t.id)}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 20px", background: "transparent", border: "none", borderBottom: isA ? `2px solid ${THEME.accent}` : "2px solid transparent", color: isA ? THEME.accent : THEME.textDim, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>
                <t.icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* CONTENT */}
        <div style={{ flex: 1, overflow: "auto", padding: "24px 28px" }}>
          {error && (
            <div style={{ padding: "12px 16px", marginBottom: 16, background: THEME.dangerDim, border: `1px solid ${THEME.danger}40`, borderRadius: 8, color: THEME.danger, fontSize: 13, display: "flex", alignItems: "center", gap: 10 }}>
              <lucide.AlertCircle size={16} />{error}
            </div>
          )}

          {activeTab === "properties" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={labelStyle}>{fmt(lang, "Titre", "Title")} *</label>
                <input value={props.title} onChange={e => setProps(p => ({ ...p, title: e.target.value }))} style={fieldStyle} />
              </div>
              <div>
                <label style={labelStyle}>{fmt(lang, "Dossier", "Folder")}</label>
                <select value={selectedFolderId} onChange={e => setSelectedFolderId(e.target.value)} style={fieldStyle}>
                  <option value="">{fmt(lang, "Racine (aucun dossier)", "Root (no folder)")}</option>
                  {folderOptions().map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>{fmt(lang, "Auteur", "Author")}</label>
                  <input value={props.author} disabled style={{ ...fieldStyle, opacity: 0.6, cursor: "not-allowed" }} />
                </div>
                <div>
                  <label style={labelStyle}>{fmt(lang, "Département", "Department")}</label>
                  <select value={props.department_id} onChange={e => setProps(p => ({ ...p, department_id: e.target.value }))} style={fieldStyle}>
                    <option value="">—</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{lang === "fr" ? (d.name_fr || d.name) : (d.name_en || d.name)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>{fmt(lang, "Classe/Catégorie", "Class/Category")}</label>
                  <select value={props.doc_class} onChange={e => setProps(p => ({ ...p, doc_class: e.target.value }))} style={fieldStyle}>
                    <option value="">—</option>
                    {DOC_CLASSES.map(c => <option key={c.value} value={c.value}>{fmt(lang, c.labelFr, c.labelEn)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>{fmt(lang, "Date de rétention", "Retention Date")}</label>
                  <input type="date" value={props.retention_date} onChange={e => setProps(p => ({ ...p, retention_date: e.target.value }))} style={fieldStyle} />
                </div>
              </div>

              <div>
                <label style={labelStyle}>{fmt(lang, "Confidentialité", "Confidentiality")}</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
                  {CONFIDENTIALITY_LEVELS.map(c => {
                    const Icon = lucide[c.icon] || lucide.Shield;
                    const isSel = props.confidentiality === c.value;
                    return (
                      <button key={c.value} type="button" onClick={() => setProps(p => ({ ...p, confidentiality: c.value }))}
                        style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "12px 8px", background: isSel ? c.color + "1A" : THEME.surface, border: `1.5px solid ${isSel ? c.color : THEME.border}`, borderRadius: 8, cursor: "pointer", transition: "all 0.15s", fontFamily: FONT }}>
                        <Icon size={18} color={isSel ? c.color : THEME.textMuted} />
                        <span style={{ fontSize: 11, fontWeight: 700, color: isSel ? c.color : THEME.textMuted, textTransform: "uppercase" }}>{fmt(lang, c.labelFr, c.labelEn)}</span>
                      </button>
                    );
                  })}
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: THEME.textDim, textAlign: "center" }}>{fmt(lang, conf.desc.fr, conf.desc.en)}</div>
              </div>

              <div>
                <label style={labelStyle}>{fmt(lang, "Mots-clés", "Keywords")}</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  {props.keywords.map(k => (
                    <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 20, background: THEME.accentDim, color: THEME.accent, fontSize: 11, fontWeight: 600 }}>
                      {k}<lucide.X size={11} style={{ cursor: "pointer" }} onClick={() => removeKeyword(k)} />
                    </span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <input value={keywordInput} onChange={e => setKeywordInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }} placeholder={fmt(lang, "Ajouter un mot-clé...", "Add keyword...")} style={{ ...fieldStyle, flex: 1 }} />
                  <button type="button" onClick={addKeyword} disabled={!keywordInput.trim()} style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 18px", background: keywordInput.trim() ? THEME.accent : THEME.accentDim, color: keywordInput.trim() ? "#fff" : THEME.accent, border: "none", borderRadius: 8, cursor: keywordInput.trim() ? "pointer" : "default", fontWeight: 600, fontSize: 13, fontFamily: FONT, whiteSpace: "nowrap", opacity: keywordInput.trim() ? 1 : 0.7, transition: "all 0.15s" }}>
                    <lucide.Plus size={14} /> {fmt(lang, "Ajouter", "Add")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === "security" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ padding: 14, background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 10, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: conf.color + "1A", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <lucide.Shield size={20} color={conf.color} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: THEME.text }}>{fmt(lang, "Sécurité par défaut", "Default Security")}: {fmt(lang, conf.labelFr, conf.labelEn)}</div>
                  <div style={{ fontSize: 11, color: THEME.textDim, marginTop: 2 }}>{fmt(lang, "Les utilisateurs non listés ci-dessous n'auront pas accès au document.", "Users not listed below will have no access to this document.")}</div>
                </div>
              </div>

              <div>
                <label style={labelStyle}>{fmt(lang, "Ajouter des utilisateurs/groupes", "Add Users/Groups")}</label>
                <PrincipalPicker api={api} lang={lang} THEME={THEME} onAdd={addPrincipal} excludeIds={excludeIds} />
              </div>

              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: THEME.textMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.3 }}>
                  {aclEntries.length} {fmt(lang, "principaux avec permissions", "principals with permissions")}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {aclEntries.map((entry, idx) => (
                    <AclEntryRow
                      key={`${entry.principal_type}-${entry.principal_id}-${idx}`}
                      entry={entry}
                      onChangeAccess={lvl => updatePrincipalAccess(idx, lvl)}
                      onRemove={() => removePrincipal(idx)}
                      lang={lang}
                      THEME={THEME}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${THEME.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: THEME.surface }}>
          <div style={{ fontSize: 11, color: THEME.textDim }}>
            {fmt(lang, "Champs requis marqués d'un *", "Required fields marked with *")}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" onClick={onClose} disabled={uploading} style={{ padding: "10px 20px", background: "transparent", color: THEME.textMuted, border: `1px solid ${THEME.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: uploading ? "not-allowed" : "pointer", opacity: uploading ? 0.4 : 1, fontFamily: FONT }}>
              {fmt(lang, "Annuler", "Cancel")}
            </button>
            <button type="button" onClick={handleUpload} disabled={uploading} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 24px", background: `linear-gradient(135deg, ${THEME.accent}, ${THEME.accent}CC)`, color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: uploading ? "wait" : "pointer", boxShadow: `0 4px 16px ${THEME.accent}40`, fontFamily: FONT, opacity: uploading ? 0.7 : 1 }}>
              {uploading ? <lucide.Loader2 size={15} className="spin" /> : <lucide.Check size={15} />}
              {uploading ? fmt(lang, "Téléversement...", "Uploading...") : fmt(lang, "Enregistrer & Téléverser", "Save & Upload")}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
